import {
  Amphora,
  ChevronDown,
  FlaskConical,
  MessageCircle
} from 'lucide-react';
import { ReactNode, Ref, useId, useRef } from 'react';
import { ToggleButton, ToggleButtonGroup } from 'react-aria-components';
import { useTranslation } from 'react-i18next';
import { Badge } from 'v5/design-system/Badge';
import { focusRings } from 'v5/design-system/_common/focusRing';
import {
  Popover,
  PopoverContent,
  PopoverTrigger
} from 'v5/design-system/Popover';
import { cn } from 'v5/platform/dom/cn';

export interface VersionSwitchPopoverProps {
  isNewVersion: boolean;
  onSwitch: (useNewVersion: boolean) => void;
  title: ReactNode;
  newVersionDescription: ReactNode;
  oldVersionDescription: ReactNode;
  newVersionPreview: ReactNode;
  oldVersionPreview: ReactNode;
  /** Opens the support conversation ("Send feedback"). */
  onSendFeedback: () => void;
}

/**
 * Compact "Beta" / "Classic" pill for header actions; opens a picker between
 * the new and the classic version of a feature. For narrow surfaces where a
 * full-width `VersionSwitchBanner` is too loud.
 */
export function VersionSwitchPopover({
  isNewVersion,
  onSwitch,
  title,
  newVersionDescription,
  oldVersionDescription,
  newVersionPreview,
  oldVersionPreview,
  onSendFeedback
}: VersionSwitchPopoverProps) {
  const { t } = useTranslation('common-widgets');
  const titleId = useId();
  const checkedItemRef = useRef<HTMLButtonElement>(null);

  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          type="button"
          aria-label={`${t('VersionSwitch.ariaLabel')}: ${
            isNewVersion ? t('VersionSwitch.beta') : t('VersionSwitch.classic')
          }`}
          className={cn(
            focusRings.focus.visible,
            'inline-flex h-7 shrink-0 cursor-pointer items-center gap-1.5 self-center rounded-full border px-2.5 text-xs font-semibold transition-colors',
            isNewVersion
              ? 'border-border-preview bg-bg-preview text-fg-preview hocus:border-border-preview-emphasis'
              : 'border-border-default bg-bg-default text-fg-default hocus:bg-bg-subtle'
          )}
        >
          {isNewVersion ? (
            <FlaskConical className="size-3.5" />
          ) : (
            <Amphora className="size-3.5" />
          )}
          {isNewVersion ? t('VersionSwitch.beta') : t('VersionSwitch.classic')}
          <ChevronDown className="size-3" />
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="end"
        aria-labelledby={titleId}
        onOpenAutoFocus={event => {
          event.preventDefault();
          checkedItemRef.current?.focus();
        }}
        className="flex w-80 flex-col gap-3.5 rounded-xl p-4"
      >
        <div className="flex flex-col gap-1.5">
          <div
            id={titleId}
            className="flex items-center gap-2 text-sm font-semibold"
          >
            <span className="grid size-6 place-items-center rounded-md bg-bg-preview text-fg-preview">
              <FlaskConical className="size-3.5" />
            </span>
            {title}
          </div>
          {/* Both stay laid out, so switching keeps the picker's height. */}
          <div className="grid text-xs leading-relaxed text-fg-muted">
            <p
              className={cn(
                'col-start-1 row-start-1 m-0',
                !isNewVersion && 'invisible'
              )}
            >
              {newVersionDescription}
            </p>
            <p
              className={cn(
                'col-start-1 row-start-1 m-0',
                isNewVersion && 'invisible'
              )}
            >
              {oldVersionDescription}
            </p>
          </div>
        </div>
        {/* Arrows only move focus; Enter, Space or a click picks, since
            picking reloads the feature. */}
        <ToggleButtonGroup
          aria-label={t('VersionSwitch.ariaLabel')}
          selectionMode="single"
          disallowEmptySelection
          selectedKeys={[isNewVersion ? 'new' : 'classic']}
          // Stays open: the pill and the cards show the switch at once.
          onSelectionChange={keys => onSwitch(keys.has('new'))}
          className="grid grid-cols-2 gap-2.5"
        >
          <VersionCard
            value="new"
            itemRef={isNewVersion ? checkedItemRef : undefined}
            label={t('VersionSwitch.new')}
            badge={
              <Badge variant="preview" tone="subtle" size="sm">
                {t('VersionSwitch.beta')}
              </Badge>
            }
            preview={newVersionPreview}
          />
          <VersionCard
            value="classic"
            itemRef={isNewVersion ? undefined : checkedItemRef}
            label={t('VersionSwitch.classic')}
            preview={oldVersionPreview}
          />
        </ToggleButtonGroup>
        <div className="flex items-center justify-between gap-2 border-t border-border-subtle pt-3 text-xs">
          <span className="text-fg-muted">
            {t('VersionSwitch.switchBackAnytime')}
          </span>
          <button
            type="button"
            onClick={onSendFeedback}
            className={cn(
              focusRings.focus.visible,
              'inline-flex cursor-pointer items-center gap-1 rounded font-semibold text-fg-preview hocus:underline'
            )}
          >
            <MessageCircle className="size-3.5" />
            {t('VersionSwitch.sendFeedback')}
          </button>
        </div>
      </PopoverContent>
    </Popover>
  );
}

function VersionCard({
  value,
  itemRef,
  label,
  badge,
  preview
}: {
  value: string;
  itemRef?: Ref<HTMLButtonElement>;
  label: ReactNode;
  badge?: ReactNode;
  preview: ReactNode;
}) {
  return (
    <ToggleButton
      ref={itemRef}
      id={value}
      className={cn(
        focusRings.focus.visible,
        'flex flex-col gap-2 rounded-xl border-2 border-border-default bg-bg-default p-2 text-left transition-colors not-data-selected:hocus:border-border-muted',
        'data-selected:border-border-preview-emphasis data-selected:bg-bg-preview-2'
      )}
    >
      <span
        aria-hidden
        className="flex h-[74px] flex-col gap-1 overflow-clip rounded-md border border-border-default bg-bg-default p-1.5"
      >
        {preview}
      </span>
      <span className="flex items-center justify-between gap-1.5 px-0.5 text-[13px] font-semibold">
        {label}
        {badge}
      </span>
    </ToggleButton>
  );
}
